// ============================================================================
// REQUEST TYPE FORM MODAL - Digital Approval System
// ============================================================================
// Modal form for creating and editing request types with file upload settings
// Follows the tab-based UI design pattern from ConsolidatedRequestModal
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import type {
  RequestTypeEntity,
  CreateRequestTypeRequest,
  UpdateRequestTypeRequest,
} from 'api/types';

// ============================================================================
// TYPES
// ============================================================================

type ModalMode = 'create' | 'edit' | 'view';
type TabType = 'basic' | 'fileSettings' | 'review';

interface RequestTypeFormModalProps {
  mode: ModalMode;
  requestType: RequestTypeEntity | null;
  commonExtensions: string[];
  onClose: () => void;
  onSave: (data: CreateRequestTypeRequest | UpdateRequestTypeRequest) => Promise<void>;
  loading?: boolean;
}

interface FormData {
  requestTypeCode: string;
  requestTypeName: string;
  description: string;
  iconName: string;
  colorCode: string;
  requiresAttachment: boolean;
  requiresBudget: boolean;
  requiresValue: boolean;
  maxFileSizeKB: string;
  maxTotalSizeKB: string;
  allowedExtensions: string[];
  isActive: boolean;
}

interface FormErrors {
  requestTypeCode?: string;
  requestTypeName?: string;
  description?: string;
  maxFileSizeKB?: string;
  maxTotalSizeKB?: string;
}

// ============================================================================
// ICON PREVIEW COMPONENT
// ============================================================================

const IconPreview: React.FC<{ iconName: string; colorCode: string }> = ({ iconName, colorCode }) => {
  const IconComponent = (IconSystem as any)[iconName];

  if (!IconComponent) {
    return (
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '8px',
          backgroundColor: colorCode || '#3b82f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '20px',
          fontWeight: 600,
        }}
      >
        ?
      </div>
    );
  }

  return (
    <div
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        backgroundColor: colorCode || '#3b82f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconComponent size={24} color="white" />
    </div>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

const RequestTypeFormModal: React.FC<RequestTypeFormModalProps> = ({
  mode,
  requestType,
  commonExtensions,
  onClose,
  onSave,
  loading = false,
}) => {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    requestTypeCode: '',
    requestTypeName: '',
    description: '',
    iconName: '',
    colorCode: '#3b82f6',
    requiresAttachment: false,
    requiresBudget: false,
    requiresValue: false,
    maxFileSizeKB: '',
    maxTotalSizeKB: '',
    allowedExtensions: [],
    isActive: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [customExtension, setCustomExtension] = useState('');

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (requestType && (mode === 'edit' || mode === 'view')) {
      setFormData({
        requestTypeCode: requestType.requestTypeCode || '',
        requestTypeName: requestType.requestTypeName || '',
        description: requestType.description || '',
        iconName: requestType.iconName || '',
        colorCode: requestType.colorCode || '#3b82f6',
        requiresAttachment: requestType.requiresAttachment || false,
        requiresBudget: requestType.requiresBudget || false,
        requiresValue: requestType.requiresValue || false,
        maxFileSizeKB: requestType.maxFileSizeKB?.toString() || '',
        maxTotalSizeKB: requestType.maxTotalSizeKB?.toString() || '',
        allowedExtensions: requestType.allowedExtensions || [],
        isActive: requestType.isActive ?? true,
      });
    }
  }, [requestType, mode]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

      setFormData((prev) => ({ ...prev, [name]: newValue }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    []
  );

  const handleExtensionToggle = useCallback((ext: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedExtensions: prev.allowedExtensions.includes(ext)
        ? prev.allowedExtensions.filter((e) => e !== ext)
        : [...prev.allowedExtensions, ext],
    }));
  }, []);

  const handleAddCustomExtension = useCallback(() => {
    let ext = customExtension.trim().toLowerCase();
    if (!ext) return;

    // Ensure it starts with a dot
    if (!ext.startsWith('.')) {
      ext = '.' + ext;
    }

    // Validate format
    if (!/^\.[a-z0-9]+$/.test(ext)) {
      return;
    }

    if (!formData.allowedExtensions.includes(ext)) {
      setFormData((prev) => ({
        ...prev,
        allowedExtensions: [...prev.allowedExtensions, ext],
      }));
    }
    setCustomExtension('');
  }, [customExtension, formData.allowedExtensions]);

  const handleRemoveExtension = useCallback((ext: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedExtensions: prev.allowedExtensions.filter((e) => e !== ext),
    }));
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (mode === 'create' && !formData.requestTypeCode.trim()) {
      newErrors.requestTypeCode = 'Request type code is required';
    } else if (mode === 'create' && !/^[A-Za-z][A-Za-z0-9_]*$/.test(formData.requestTypeCode)) {
      newErrors.requestTypeCode =
        'Code must start with a letter and contain only letters, numbers, and underscores';
    }

    if (!formData.requestTypeName.trim()) {
      newErrors.requestTypeName = 'Request type name is required';
    }

    if (formData.maxFileSizeKB && (isNaN(Number(formData.maxFileSizeKB)) || Number(formData.maxFileSizeKB) <= 0)) {
      newErrors.maxFileSizeKB = 'Must be a positive number';
    }

    if (formData.maxTotalSizeKB && (isNaN(Number(formData.maxTotalSizeKB)) || Number(formData.maxTotalSizeKB) <= 0)) {
      newErrors.maxTotalSizeKB = 'Must be a positive number';
    }

    setErrors(newErrors);

    // Switch to tab with errors
    if (newErrors.requestTypeCode || newErrors.requestTypeName) {
      setActiveTab('basic');
    } else if (newErrors.maxFileSizeKB || newErrors.maxTotalSizeKB) {
      setActiveTab('fileSettings');
    }

    return Object.keys(newErrors).length === 0;
  }, [formData, mode]);

  const handleShowConfirmation = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (mode === 'view') return;
      if (!validate()) return;

      // Show confirmation dialog before submitting
      setShowConfirmDialog(true);
    },
    [mode, validate]
  );

  const handleConfirmSubmit = useCallback(async () => {
    setShowConfirmDialog(false);

    const baseData = {
      requestTypeName: formData.requestTypeName.trim(),
      description: formData.description.trim() || undefined,
      iconName: formData.iconName.trim() || undefined,
      colorCode: formData.colorCode || undefined,
      requiresAttachment: formData.requiresAttachment,
      requiresBudget: formData.requiresBudget,
      requiresValue: formData.requiresValue,
      maxFileSizeKB: formData.maxFileSizeKB ? Number(formData.maxFileSizeKB) : null,
      maxTotalSizeKB: formData.maxTotalSizeKB ? Number(formData.maxTotalSizeKB) : null,
      allowedExtensions: formData.allowedExtensions.length > 0 ? formData.allowedExtensions : null,
      isActive: formData.isActive,
    };

    if (mode === 'create') {
      const createData: CreateRequestTypeRequest = {
        ...baseData,
        requestTypeCode: formData.requestTypeCode.trim(),
      };
      await onSave(createData);
    } else if (mode === 'edit' && requestType) {
      const updateData: UpdateRequestTypeRequest = {
        ...baseData,
        rowVersion: requestType.rowVersion,
      };
      await onSave(updateData);
    }
  }, [formData, mode, requestType, onSave]);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // ---------------------------------------------------------------------------
  // COMPUTED
  // ---------------------------------------------------------------------------

  const isReadOnly = mode === 'view';
  const title =
    mode === 'create'
      ? 'Create Request Type'
      : mode === 'edit'
      ? 'Edit Request Type'
      : 'View Request Type';

  const hasBasicErrors = !!(errors.requestTypeCode || errors.requestTypeName);
  const hasFileSettingsErrors = !!(errors.maxFileSizeKB || errors.maxTotalSizeKB);

  // ---------------------------------------------------------------------------
  // FORMAT HELPERS
  // ---------------------------------------------------------------------------

  const formatFileSize = (kb: string): string => {
    if (!kb) return 'Global default';
    const kbNum = Number(kb);
    if (kbNum >= 1024) {
      return `${(kbNum / 1024).toFixed(1)} MB`;
    }
    return `${kbNum} KB`;
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const modalContent = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' && <IconSystem.add size={20} style={{ marginRight: '8px' }} />}
            {mode === 'edit' && <IconSystem.edit size={20} style={{ marginRight: '8px' }} />}
            {mode === 'view' && <IconSystem.eye size={20} style={{ marginRight: '8px' }} />}
            {title}
          </h2>
          <button className="modal-close" onClick={onClose} type="button">
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: 0 }}>
          <form onSubmit={handleShowConfirmation}>
            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button
                type="button"
                className={`tab-button ${activeTab === 'basic' ? 'active' : ''} ${hasBasicErrors ? 'has-error' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                <IconSystem.file size={16} />
                <span>Basic Information</span>
                {hasBasicErrors && <span className="error-indicator">!</span>}
              </button>
              {/* File Upload Settings - Only show when Requires Attachment is checked */}
              {formData.requiresAttachment && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'fileSettings' ? 'active' : ''} ${hasFileSettingsErrors ? 'has-error' : ''}`}
                  onClick={() => setActiveTab('fileSettings')}
                >
                  <IconSystem.upload size={16} />
                  <span>File Upload Settings</span>
                  {hasFileSettingsErrors && <span className="error-indicator">!</span>}
                </button>
              )}
              <button
                type="button"
                className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
                onClick={() => setActiveTab('review')}
              >
                <IconSystem.eye size={16} />
                <span>Review {!isReadOnly && '& Save'}</span>
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Tab 1: Basic Information */}
              {activeTab === 'basic' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.file size={18} />
                      Request Type Identity
                    </h3>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Request Type Code <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          name="requestTypeCode"
                          value={formData.requestTypeCode}
                          onChange={handleChange}
                          disabled={isReadOnly || mode === 'edit'}
                          placeholder="e.g., LEAVE_REQUEST"
                          className={`form-input ${errors.requestTypeCode ? 'error' : ''} ${isReadOnly || mode === 'edit' ? 'disabled' : ''}`}
                        />
                        {errors.requestTypeCode && (
                          <span className="form-error">{errors.requestTypeCode}</span>
                        )}
                        <span className="form-hint">
                          Uppercase letters, numbers, and underscores only
                        </span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Request Type Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          name="requestTypeName"
                          value={formData.requestTypeName}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          placeholder="e.g., Leave Request"
                          className={`form-input ${errors.requestTypeName ? 'error' : ''}`}
                        />
                        {errors.requestTypeName && (
                          <span className="form-error">{errors.requestTypeName}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        rows={3}
                        placeholder="Enter a description for this request type..."
                        className="form-textarea"
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.settings size={18} />
                      Appearance
                    </h3>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Icon Name</label>
                        <input
                          type="text"
                          name="iconName"
                          value={formData.iconName}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          placeholder="e.g., calendar, file, user"
                          className="form-input"
                        />
                        <span className="form-hint">
                          Available icons: file, calendar, user, check, settings, etc.
                        </span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Color Code</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            name="colorCode"
                            value={formData.colorCode}
                            onChange={handleChange}
                            disabled={isReadOnly}
                            className="color-picker"
                          />
                          <input
                            type="text"
                            name="colorCode"
                            value={formData.colorCode}
                            onChange={handleChange}
                            disabled={isReadOnly}
                            className="form-input color-text"
                            style={{ fontFamily: 'monospace' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Section */}
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.check size={18} />
                      Status
                    </h3>

                    <div className="requirements-checkboxes">
                      <label className={`requirement-checkbox-item ${formData.isActive ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        />
                        {formData.isActive ? (
                          <IconSystem.check size={18} style={{ color: '#10b981' }} />
                        ) : (
                          <IconSystem.x size={18} style={{ color: '#ef4444' }} />
                        )}
                        <span>{formData.isActive ? 'Active' : 'Inactive'}</span>
                      </label>
                    </div>
                  </div>

                  {/* Request Requirements Section */}
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.settings size={18} />
                      Request Requirements
                    </h3>
                    <p className="form-section-description">
                      Configure what information is required when creating requests of this type.
                    </p>

                    <div className="requirements-checkboxes">
                      {/* Requires Attachment */}
                      <label className={`requirement-checkbox-item ${formData.requiresAttachment ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          name="requiresAttachment"
                          checked={formData.requiresAttachment}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        />
                        <IconSystem.upload size={18} />
                        <span>Requires Attachment</span>
                      </label>

                      {/* Requires Value */}
                      <label className={`requirement-checkbox-item ${formData.requiresValue ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          name="requiresValue"
                          checked={formData.requiresValue}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        />
                        <IconSystem.file size={18} />
                        <span>Requires Value</span>
                      </label>
                    </div>

                    {formData.requiresAttachment && (
                      <div className="info-box" style={{ marginTop: '12px' }}>
                        <IconSystem.warning size={16} />
                        <span>
                          File upload settings can be configured in the <strong>File Upload Settings</strong> tab.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 2: File Upload Settings - Only shown when Requires Attachment is checked */}
              {activeTab === 'fileSettings' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.upload size={18} />
                      File Size Limits
                    </h3>
                    <p className="form-section-description">
                      Configure file upload limits for this request type. Leave empty to use global defaults.
                    </p>

                    <div className="info-box">
                      <IconSystem.warning size={16} />
                      <span>
                        Global defaults: <strong>100 KB</strong> per file, <strong>1,024 KB</strong> total
                      </span>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Max File Size (KB)</label>
                        <input
                          type="number"
                          name="maxFileSizeKB"
                          value={formData.maxFileSizeKB}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          placeholder="e.g., 5120 (5 MB)"
                          min="1"
                          className={`form-input ${errors.maxFileSizeKB ? 'error' : ''}`}
                        />
                        {errors.maxFileSizeKB && (
                          <span className="form-error">{errors.maxFileSizeKB}</span>
                        )}
                        <span className="form-hint">
                          Maximum size for a single file: {formatFileSize(formData.maxFileSizeKB)}
                        </span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Max Total Size (KB)</label>
                        <input
                          type="number"
                          name="maxTotalSizeKB"
                          value={formData.maxTotalSizeKB}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          placeholder="e.g., 10240 (10 MB)"
                          min="1"
                          className={`form-input ${errors.maxTotalSizeKB ? 'error' : ''}`}
                        />
                        {errors.maxTotalSizeKB && (
                          <span className="form-error">{errors.maxTotalSizeKB}</span>
                        )}
                        <span className="form-hint">
                          Maximum total size for all files: {formatFileSize(formData.maxTotalSizeKB)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.file size={18} />
                      Allowed Extensions
                    </h3>
                    <p className="form-section-description">
                      Select allowed file extensions. Leave empty to use global defaults.
                    </p>

                    {/* Selected Extensions */}
                    {formData.allowedExtensions.length > 0 && (
                      <div className="selected-extensions">
                        <label className="form-label">Selected Extensions:</label>
                        <div className="extension-tags">
                          {formData.allowedExtensions.map((ext) => (
                            <span key={ext} className="extension-tag">
                              {ext}
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  className="remove-ext-btn"
                                  onClick={() => handleRemoveExtension(ext)}
                                >
                                  <IconSystem.x size={12} />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Common Extensions */}
                    {!isReadOnly && (
                      <>
                        <div className="extension-categories">
                          <div className="extension-category">
                            <h4>Documents</h4>
                            <div className="extension-grid">
                              {['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt'].map((ext) => (
                                <label key={ext} className={`extension-option ${formData.allowedExtensions.includes(ext) ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={formData.allowedExtensions.includes(ext)}
                                    onChange={() => handleExtensionToggle(ext)}
                                  />
                                  <span>{ext}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="extension-category">
                            <h4>Images</h4>
                            <div className="extension-grid">
                              {['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp'].map((ext) => (
                                <label key={ext} className={`extension-option ${formData.allowedExtensions.includes(ext) ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={formData.allowedExtensions.includes(ext)}
                                    onChange={() => handleExtensionToggle(ext)}
                                  />
                                  <span>{ext}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="extension-category">
                            <h4>Archives</h4>
                            <div className="extension-grid">
                              {['.zip', '.rar', '.7z', '.tar', '.gz'].map((ext) => (
                                <label key={ext} className={`extension-option ${formData.allowedExtensions.includes(ext) ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={formData.allowedExtensions.includes(ext)}
                                    onChange={() => handleExtensionToggle(ext)}
                                  />
                                  <span>{ext}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Custom Extension Input */}
                        <div className="custom-extension-input">
                          <label className="form-label">Add Custom Extension</label>
                          <div className="input-with-button">
                            <input
                              type="text"
                              value={customExtension}
                              onChange={(e) => setCustomExtension(e.target.value)}
                              placeholder="e.g., .xyz"
                              className="form-input"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomExtension();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddCustomExtension}
                              className="btn btn-secondary"
                            >
                              <IconSystem.add size={16} />
                              Add
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 3: Review */}
              {activeTab === 'review' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.eye size={18} />
                      Review Configuration
                    </h3>
                    <p className="form-section-description">
                      Review all settings before {mode === 'create' ? 'creating' : 'saving'} this request type.
                    </p>

                    {/* Preview Card */}
                    <div className="review-preview-card">
                      <IconPreview iconName={formData.iconName} colorCode={formData.colorCode} />
                      <div className="review-preview-info">
                        <h4>{formData.requestTypeName || 'Request Type Name'}</h4>
                        <span className="code-badge">{formData.requestTypeCode || 'REQUEST_CODE'}</span>
                        <span className={`status-badge ${formData.isActive ? 'active' : 'inactive'}`}>
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {formData.description && (
                      <div className="review-section">
                        <h4>Description</h4>
                        <p>{formData.description}</p>
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.check size={18} />
                      Requirements Summary
                    </h3>

                    <div className="review-grid">
                      <div className={`review-item ${formData.requiresAttachment ? 'enabled' : 'disabled'}`}>
                        <IconSystem.upload size={20} />
                        <span>Attachment Required</span>
                        <span className="review-status">
                          {formData.requiresAttachment ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className={`review-item ${formData.requiresValue ? 'enabled' : 'disabled'}`}>
                        <IconSystem.file size={20} />
                        <span>Value Required</span>
                        <span className="review-status">
                          {formData.requiresValue ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.upload size={18} />
                      File Upload Settings
                    </h3>

                    <div className="review-grid">
                      <div className="review-item">
                        <IconSystem.file size={20} />
                        <span>Max File Size</span>
                        <span className="review-value">
                          {formData.maxFileSizeKB ? formatFileSize(formData.maxFileSizeKB) : 'Global default'}
                        </span>
                      </div>
                      <div className="review-item">
                        <IconSystem.file size={20} />
                        <span>Max Total Size</span>
                        <span className="review-value">
                          {formData.maxTotalSizeKB ? formatFileSize(formData.maxTotalSizeKB) : 'Global default'}
                        </span>
                      </div>
                    </div>

                    {formData.allowedExtensions.length > 0 && (
                      <div className="review-section">
                        <h4>Allowed Extensions</h4>
                        <div className="extension-tags">
                          {formData.allowedExtensions.map((ext) => (
                            <span key={ext} className="extension-tag readonly">
                              {ext}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.allowedExtensions.length === 0 && (
                      <div className="info-box">
                        <IconSystem.warning size={16} />
                        <span>Using global default extensions</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </form>
        </div>

        {/* Modal Footer - Outside modal-content for fixed position */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button type="submit" disabled={loading} className="btn btn-primary" onClick={handleShowConfirmation}>
              {loading ? (
                <>
                  <IconSystem.refresh size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                <>
                  <IconSystem.check size={16} />
                  {mode === 'create' ? 'Create Request Type' : 'Save Changes'}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Scoped Styles */}
      <style>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1000;
        }

        .modal-container {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          z-index: 1001;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .modal-lg {
          width: 90%;
          max-width: 800px;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          display: flex;
          align-items: center;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          color: #6b7280;
          transition: all 0.2s;
        }

        .modal-close:hover {
          background-color: #f3f4f6;
          color: #1f2937;
        }

        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 0 24px 24px;
          min-height: 0;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0 0 12px 12px;
          flex-shrink: 0;
        }

        /* Tab Navigation */
        .tab-navigation {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #e5e7eb;
          padding: 16px 0 0;
          margin-bottom: 24px;
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-size: 14px;
          color: #6b7280;
          transition: all 0.2s;
          margin-bottom: -1px;
        }

        .tab-button:hover {
          color: #1f2937;
          background-color: #f9fafb;
        }

        .tab-button.active {
          color: #2563eb;
          border-bottom-color: #2563eb;
        }

        .tab-button.has-error {
          color: #dc2626;
        }

        .tab-button.has-error.active {
          border-bottom-color: #dc2626;
        }

        .error-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          background-color: #dc2626;
          color: white;
          border-radius: 50%;
          font-size: 11px;
          font-weight: bold;
        }

        /* Form Sections */
        .form-section {
          margin-bottom: 24px;
        }

        .form-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section-description {
          font-size: 14px;
          color: #6b7280;
          margin: 0 0 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 640px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .required {
          color: #dc2626;
        }

        .form-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db !important;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
          background-color: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .form-input.error {
          border-color: #dc2626 !important;
        }

        .form-input.disabled,
        .form-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
          border-color: #e5e7eb !important;
        }

        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db !important;
          border-radius: 6px;
          font-size: 14px;
          resize: vertical;
          min-height: 80px;
          box-sizing: border-box;
          background-color: white;
          font-family: inherit;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .form-textarea:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
          border-color: #e5e7eb !important;
        }

        /* Ensure number inputs also have borders */
        input[type="number"].form-input {
          border: 1px solid #d1d5db !important;
        }

        .form-error {
          display: block;
          font-size: 12px;
          color: #dc2626;
          margin-top: 4px;
        }

        .form-hint {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }

        /* Color Input */
        .color-input-wrapper {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .color-picker {
          width: 48px;
          height: 40px;
          padding: 2px;
          border: 1px solid #d1d5db !important;
          border-radius: 6px;
          cursor: pointer;
          background-color: white;
        }

        .color-picker:focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .color-text {
          flex: 1;
        }

        /* Preview Box */
        .preview-box {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .preview-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .preview-name {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .preview-code {
          font-size: 12px;
          color: #6b7280;
          font-family: monospace;
        }

        /* Requirements Grid */
        .requirements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
        }

        .requirement-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .requirement-card:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        .requirement-card.selected {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .requirement-card.wide {
          grid-column: 1 / -1;
        }

        .requirement-card.active-status.selected {
          border-color: #10b981;
          background: #ecfdf5;
        }

        .requirement-card.inactive-status:not(.selected) {
          border-color: #fecaca;
          background: #fef2f2;
        }

        .requirement-checkbox {
          display: none;
        }

        .requirement-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 8px;
          color: #6b7280;
        }

        .requirement-card.selected .requirement-icon {
          background: #dbeafe;
          color: #2563eb;
        }

        .requirement-info {
          flex: 1;
        }

        .requirement-title {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .requirement-description {
          display: block;
          font-size: 13px;
          color: #6b7280;
        }

        /* Info Box */
        .info-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 6px;
          font-size: 14px;
          color: #92400e;
          margin-bottom: 16px;
        }

        /* Extension Categories */
        .extension-categories {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 16px;
        }

        .extension-category h4 {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px;
        }

        .extension-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .extension-option {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          font-size: 13px;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        .extension-option:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        .extension-option.selected {
          border-color: #2563eb;
          background: #dbeafe;
          color: #1e40af;
        }

        .extension-option input {
          display: none;
        }

        /* Selected Extensions */
        .selected-extensions {
          margin-bottom: 16px;
        }

        .extension-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .extension-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 16px;
          font-size: 13px;
        }

        .extension-tag.readonly {
          background: #f3f4f6;
          color: #374151;
        }

        .remove-ext-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 2px;
          color: #1e40af;
          border-radius: 50%;
          transition: background 0.2s;
        }

        .remove-ext-btn:hover {
          background: rgba(30, 64, 175, 0.1);
        }

        /* Custom Extension Input */
        .custom-extension-input {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .input-with-button {
          display: flex;
          gap: 8px;
        }

        .input-with-button .form-input {
          flex: 1;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .btn-primary {
          background: #2563eb;
          color: white;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #e5e7eb;
        }

        /* Review Section */
        .review-preview-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-radius: 12px;
          margin-bottom: 16px;
        }

        .review-preview-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .review-preview-info h4 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .code-badge {
          display: inline-block;
          padding: 2px 8px;
          background: #e5e7eb;
          border-radius: 4px;
          font-size: 12px;
          font-family: monospace;
          color: #374151;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        .review-section {
          margin: 16px 0;
        }

        .review-section h4 {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px;
        }

        .review-section p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .review-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
        }

        .review-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          min-width: 0;
        }

        .review-item > span:first-of-type {
          white-space: nowrap;
        }

        .review-item.enabled {
          background: #ecfdf5;
          border-color: #10b981;
        }

        .review-item.enabled .review-status {
          color: #065f46;
        }

        .review-item.disabled {
          opacity: 0.7;
        }

        .review-status, .review-value {
          margin-left: auto;
          white-space: nowrap;
          flex-shrink: 0;
          font-weight: 500;
          color: #1f2937;
        }

        /* Animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Requirements Checkboxes - Inline style */
        .requirements-checkboxes {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .requirement-checkbox-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
          font-size: 14px;
          color: #374151;
        }

        .requirement-checkbox-item:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        .requirement-checkbox-item.selected {
          border-color: #2563eb;
          background: #eff6ff;
          color: #1e40af;
        }

        .requirement-checkbox-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #2563eb;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>

      {/* Confirmation Dialog - Rendered via Portal */}
      {showConfirmDialog && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10060,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 10061,
              width: '90%',
              maxWidth: '500px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1.25rem 1.5rem',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderBottom: '1px solid #f59e0b',
              }}
            >
              <IconSystem.warning size={24} style={{ color: '#f59e0b' }} />
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#92400e' }}>
                Confirm {mode === 'create' ? 'Creation' : 'Update'}
              </h3>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>
                Are you sure you want to {mode === 'create' ? 'create' : 'update'} this request type?
              </p>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <IconPreview iconName={formData.iconName} colorCode={formData.colorCode} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                      {formData.requestTypeName || 'Untitled'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                      {formData.requestTypeCode || 'NO_CODE'}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                  Status: <strong>{formData.isActive ? 'Active' : 'Inactive'}</strong>
                  {formData.requiresAttachment && ' • Requires Attachment'}
                  {formData.requiresValue && ' • Requires Value'}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                padding: '1rem 1.5rem',
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <button
                type="button"
                onClick={handleCancelConfirm}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={loading}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  background: loading ? '#93c5fd' : '#2563eb',
                  border: 'none',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {loading ? (
                  <>
                    <IconSystem.refresh size={16} className="spinner" />
                    {mode === 'create' ? 'Creating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <IconSystem.check size={16} />
                    {mode === 'create' ? 'Create Request Type' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );

  return createPortal(modalContent, document.body);
};

export default RequestTypeFormModal;
